import { IPublicClientApplication } from '@azure/msal-browser';
import { loginRequest } from '../authConfig';

// Types for Microsoft Graph API responses
export interface UserProfile {
  id: string;
  displayName: string;
  mail: string;
  userPrincipalName: string;
  givenName?: string;
  surname?: string;
  jobTitle?: string;
  officeLocation?: string;
  mobilePhone?: string;
  businessPhones?: string[];
}

// Configuration constants
export const GRAPH_CONFIG = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
  requestTimeout: 10000, // 10 seconds
  cacheExpiry: 5 * 60 * 1000, // 5 minutes
  maxRetries: 3,
  retryDelay: 1000, // 1 second
};

// Cache keys
const CACHE_KEYS = {
  userProfile: 'userProfileData',
  userProfileTimestamp: 'userProfileTimestamp',
};

/**
 * Delay helper for retry logic
 */
const delay = (ms: number): Promise<void> => 
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Call Microsoft Graph API with timeout and abort controller
 */
export const callMsGraph = async (
  accessToken: string,
  endpoint: string = GRAPH_CONFIG.graphMeEndpoint,
  timeout: number = GRAPH_CONFIG.requestTimeout
): Promise<UserProfile> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Microsoft Graph API error: ${response.status} ${response.statusText}`);
    }

    const data: UserProfile = await response.json();
    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error(`Microsoft Graph API call timed out after ${timeout}ms`);
      }
      throw new Error(`Microsoft Graph API call failed: ${error.message}`);
    }
    
    throw error;
  }
};

/**
 * Call Microsoft Graph API with retry logic
 */
export const callMsGraphWithRetry = async (
  accessToken: string,
  endpoint: string = GRAPH_CONFIG.graphMeEndpoint,
  maxRetries: number = GRAPH_CONFIG.maxRetries
): Promise<UserProfile> => {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const data = await callMsGraph(accessToken, endpoint);
      return data;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      console.warn(`Microsoft Graph API attempt ${attempt + 1} failed:`, lastError.message);

      if (attempt < maxRetries - 1) {
        const delayTime = GRAPH_CONFIG.retryDelay * Math.pow(2, attempt); // Exponential backoff
        await delay(delayTime);
      }
    }
  }

  throw new Error(`Microsoft Graph API failed after ${maxRetries} attempts: ${lastError?.message}`);
};

/**
 * Get cached user profile if valid
 */
export const getCachedUserProfile = (): UserProfile | null => {
  try {
    const cachedProfile = localStorage.getItem(CACHE_KEYS.userProfile);
    const cacheTimestamp = localStorage.getItem(CACHE_KEYS.userProfileTimestamp);

    if (!cachedProfile || !cacheTimestamp) {
      return null;
    }

    const now = Date.now();
    const isCacheValid = now - parseInt(cacheTimestamp, 10) < GRAPH_CONFIG.cacheExpiry;

    if (isCacheValid) {
      return JSON.parse(cachedProfile) as UserProfile;
    }

    // Clear expired cache
    clearUserProfileCache();
    return null;
  } catch (error) {
    console.error('Error reading cached user profile:', error);
    clearUserProfileCache();
    return null;
  }
};

/**
 * Cache user profile data
 */
export const cacheUserProfile = (profile: UserProfile): void => {
  try {
    localStorage.setItem(CACHE_KEYS.userProfile, JSON.stringify(profile));
    localStorage.setItem(CACHE_KEYS.userProfileTimestamp, Date.now().toString());
  } catch (error) {
    console.error('Error caching user profile:', error);
  }
};

/**
 * Clear user profile cache
 */
export const clearUserProfileCache = (): void => {
  try {
    localStorage.removeItem(CACHE_KEYS.userProfile);
    localStorage.removeItem(CACHE_KEYS.userProfileTimestamp);
  } catch (error) {
    console.error('Error clearing user profile cache:', error);
  }
};

/**
 * Get user profile with caching and retry logic
 */
export const getUserProfile = async (
  instance: IPublicClientApplication,
  authority?: string,
  useCache: boolean = true
): Promise<UserProfile> => {
  // Check cache first if enabled
  if (useCache) {
    const cachedProfile = getCachedUserProfile();
    if (cachedProfile) {
      return cachedProfile;
    }
  }

  // Get accounts
  const accounts = instance.getAllAccounts();
  if (!accounts || accounts.length === 0) {
    throw new Error('No authenticated accounts found');
  }

  // Use active account or first account
  const account = instance.getActiveAccount() || accounts[0];
  if (!account) {
    throw new Error('No account available for token acquisition');
  }

  // Acquire token silently
  try {
    const tokenResponse = await instance.acquireTokenSilent({
      scopes: ['User.Read', 'User.Read.All'],
      authority: authority,
      account: account,
    });

    // Call Microsoft Graph API with retry logic
    const userProfile = await callMsGraphWithRetry(tokenResponse.accessToken);

    // Cache the profile
    if (useCache) {
      cacheUserProfile(userProfile);
    }

    // Also store in legacy format for backward compatibility
    try {
      if (userProfile.mail) {
        localStorage.setItem('UserMail', userProfile.mail.toLowerCase());
      }
      if (userProfile.displayName) {
        localStorage.setItem('UserName', userProfile.displayName);
      }
      if (userProfile.id) {
        localStorage.setItem('UserId', userProfile.id);
      }
    } catch (error) {
      console.warn('Failed to store legacy user data:', error);
    }

    return userProfile;
  } catch (error) {
    // Try to use stale cache as fallback
    if (useCache) {
      const staleCache = localStorage.getItem(CACHE_KEYS.userProfile);
      if (staleCache) {
        console.warn('Using stale cache due to token acquisition failure');
        return JSON.parse(staleCache) as UserProfile;
      }
    }

    if (error instanceof Error) {
      throw new Error(`Failed to get user profile: ${error.message}`);
    }
    throw error;
  }
};

/**
 * Refresh user profile (bypass cache)
 */
export const refreshUserProfile = async (
  instance: IPublicClientApplication,
  authority?: string
): Promise<UserProfile> => {
  clearUserProfileCache();
  return getUserProfile(instance, authority, false);
};

/**
 * Get user profile with fallback to redirect
 */
export const getUserProfileWithRedirect = async (
  instance: IPublicClientApplication,
  authority?: string,
  useCache: boolean = true
): Promise<UserProfile | null> => {
  try {
    return await getUserProfile(instance, authority, useCache);
  } catch (error) {
    console.error('Failed to get user profile, redirecting to login:', error);
    instance.loginRedirect(loginRequest);
    return null;
  }
};
